import { useState, useEffect, useMemo, useRef } from 'react'
import { ChevronDown, Printer, BarChart3, RefreshCw } from 'lucide-react'
import leiloesService from '../../services/leiloesService'
import mapasService from '../../services/mapasService'
import { useOrganizador } from '../../contexts/OrganizadorContext'
import { downloadPdf } from '../../utils/pdfUtils'

const API_BASE = (import.meta.env.VITE_API_URL || 'http://localhost:8001').replace(/\/$/, '')

const brl = v => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0)
const fmtDate = s => { if (!s) return ''; try { return new Date(s).toLocaleDateString('pt-BR') } catch { return s } }

function Select({ label, value, onChange, children, disabled, className = '' }) {
  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      {label && <label className="text-xs text-gray-500 font-medium">{label}</label>}
      <div className="relative">
        <select value={value} onChange={e => onChange(e.target.value)} disabled={disabled}
          className="w-full appearance-none border border-gray-300 rounded-lg px-3 py-2 pr-8 text-sm bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-400">
          {children}
        </select>
        <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
      </div>
    </div>
  )
}

const S = {
  page: { fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 14, color: '#000', background: '#fff', padding: '20px 28px 24px' },
  hdr: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '2px solid #1f2937', paddingBottom: 14, marginBottom: 18 },
  hdrLogo: { maxHeight: 80, maxWidth: 180, objectFit: 'contain' },
  hdrCenter: { flex: 1, textAlign: 'center', padding: '0 16px' },
  hdrT1: { fontSize: 20, fontWeight: 700 },
  hdrT2: { fontSize: 13, color: '#374151', marginTop: 3 },
  hdrT3: { fontSize: 12, color: '#6b7280', marginTop: 2 },
  tbl: { width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 6 },
  th: { background: '#f3f4f6', border: '1px solid #d1d5db', padding: '8px 10px', textAlign: 'center', fontWeight: 700, fontSize: 12 },
  thL: { background: '#f3f4f6', border: '1px solid #d1d5db', padding: '8px 10px', textAlign: 'left', fontWeight: 700, fontSize: 12 },
  thR: { background: '#f3f4f6', border: '1px solid #d1d5db', padding: '8px 10px', textAlign: 'right', fontWeight: 700, fontSize: 12 },
  td: { border: '1px solid #e5e7eb', padding: '7px 10px', verticalAlign: 'middle' },
  tdC: { border: '1px solid #e5e7eb', padding: '7px 10px', textAlign: 'center', verticalAlign: 'middle' },
  tdR: { border: '1px solid #e5e7eb', padding: '7px 10px', textAlign: 'right', verticalAlign: 'middle' },
  secRow: { background: '#f3f4f6', fontWeight: 700, fontSize: 13 },
  totRow: { background: '#e5e7eb', fontWeight: 700, fontSize: 13 },
  footer: { textAlign: 'center', fontSize: 11, color: '#9ca3af', marginTop: 18, borderTop: '1px solid #e5e7eb', paddingTop: 10 },
}

export default function BalancoVendas() {
  const { organizadorAtivo } = useOrganizador()
  const orgId = organizadorAtivo?.id

  const [leiloes, setLeiloes] = useState([])
  const [leilaoId, setLeilaoId] = useState('')
  const [dados, setDados] = useState(null)
  const [loading, setLoading] = useState(false)
  const [loadingL, setLoadingL] = useState(true)
  const [loadingPdf, setLoadingPdf] = useState(false)
  const [pdfUrl, setPdfUrl] = useState(null)
  const [showPdf, setShowPdf] = useState(false)
  const iframeRef = useRef(null)

  useEffect(() => {
    if (!orgId) return
    setLoadingL(true)
    leiloesService.getByOrganizador(orgId)
      .then(data => {
        const sorted = [...(data || [])].sort((a, b) => new Date(b.data_leilao) - new Date(a.data_leilao))
        setLeiloes(sorted)
        if (sorted.length) setLeilaoId(String((sorted.find(l => l.status === 'on') || sorted[0]).id))
      })
      .finally(() => setLoadingL(false))
  }, [orgId])

  // Busca dados do balanço
  useEffect(() => {
    if (!orgId || !leilaoId) { setDados(null); return }
    setLoading(true)
    mapasService.getBalancoVendasDados(orgId, leilaoId)
      .then(setDados)
      .catch(() => setDados(null))
      .finally(() => setLoading(false))
  }, [orgId, leilaoId])

  const leilaoAtual = leiloes.find(l => String(l.id) === leilaoId)
  const logoOrgSrc = organizadorAtivo?.logo_url
    ? (organizadorAtivo.logo_url.startsWith('http') ? organizadorAtivo.logo_url
        : `${API_BASE}${organizadorAtivo.logo_url.startsWith('/') ? '' : '/'}${organizadorAtivo.logo_url}`)
    : null

  const handleImprimir = async () => {
    if (!orgId || !leilaoId) return
    setLoadingPdf(true)
    try {
      const blob = await mapasService.getBalancoVendasPdf(orgId, leilaoId)
      if (pdfUrl) URL.revokeObjectURL(pdfUrl)
      const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }))
      setPdfUrl(url)
      setShowPdf(true)
    } catch (err) {
      alert('Erro ao gerar PDF: ' + (err.response?.statusText || err.message))
    } finally { setLoadingPdf(false) }
  }
  const closePdf = () => { setShowPdf(false); if (pdfUrl) { URL.revokeObjectURL(pdfUrl); setPdfUrl(null) } }
  const printPdf = () => { if (iframeRef.current?.contentWindow) { iframeRef.current.contentWindow.focus(); iframeRef.current.contentWindow.print() } }

  if (!orgId) return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-700">
      Selecione um organizador para visualizar o Balanço de Vendas.
    </div>
  )

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <BarChart3 className="w-5 h-5 text-rose-500" />
        <h1 className="text-lg font-semibold text-gray-800">Balanço de Vendas</h1>
      </div>

      {/* Seletor */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <div className="flex flex-wrap items-end gap-3">
          <Select label="Leilão" value={leilaoId} onChange={setLeilaoId} disabled={loadingL} className="flex-[2] min-w-[200px]">
            {loadingL ? <option>Carregando...</option>
              : leiloes.map(l => <option key={l.id} value={String(l.id)}>{l.nome} — {fmtDate(l.data_leilao)}</option>)
            }
          </Select>
          <div className="flex items-end gap-2 ml-auto self-end">
            {loading && <RefreshCw size={15} className="text-gray-400 animate-spin mb-2" />}
            <button onClick={handleImprimir} disabled={!dados || loadingPdf}
              className="flex items-center gap-2 px-4 py-2 h-9 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-200 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors">
              <Printer size={15} />
              {loadingPdf ? 'Gerando PDF...' : 'Imprimir'}
            </button>
          </div>
        </div>
      </div>

      {/* Preview */}
      {loading ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      ) : !dados ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-400 text-sm">
          Selecione um leilão para visualizar o balanço
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div style={S.page}>
            {/* Header */}
            <div style={S.hdr}>
              <div style={{ minWidth: 140 }}>
                {logoOrgSrc ? <img src={logoOrgSrc} alt="logo" style={S.hdrLogo} />
                  : <span style={{ fontWeight: 700, fontSize: 14 }}>{organizadorAtivo?.name || organizadorAtivo?.nome || ''}</span>}
              </div>
              <div style={S.hdrCenter}>
                <div style={S.hdrT1}>Balanço de Vendas</div>
                <div style={S.hdrT2}>{organizadorAtivo?.name || organizadorAtivo?.nome || ''}</div>
                <div style={S.hdrT3}>{leilaoAtual?.nome} — {fmtDate(leilaoAtual?.data_leilao)}</div>
              </div>
              <div style={{ minWidth: 140, textAlign: 'right' }}>
                <img src="/assets/rematei.png" alt="Rematei" style={S.hdrLogo} />
              </div>
            </div>

            {/* Tabela principal */}
            <table style={S.tbl}>
              <thead>
                <tr>
                  <th style={{ ...S.thL, width: '15%' }}>Tipo</th>
                  <th style={{ ...S.thL, width: '14%' }}>Raça</th>
                  <th style={{ ...S.th, width: '10%' }}>Sexo</th>
                  <th style={{ ...S.th, width: '8%' }}>Lotes</th>
                  <th style={{ ...S.th, width: '10%' }}>Quantidade</th>
                  <th style={{ ...S.thR, width: '15%' }}>Valor</th>
                  <th style={{ ...S.thR, width: '13%' }}>Média</th>
                  <th style={{ ...S.thR, width: '13%' }}>Média R$/kg</th>
                </tr>
              </thead>
              <tbody>
                {dados.linhas.map((l, i) => (
                  <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#f9fafb' }}>
                    <td style={S.td}>{l.tipo}</td>
                    <td style={S.td}>{l.raca}</td>
                    <td style={S.tdC}>{l.sexo}</td>
                    <td style={S.tdC}>{l.lotes}</td>
                    <td style={S.tdC}><strong>{l.qtd}</strong></td>
                    <td style={S.tdR}>{brl(l.valor)}</td>
                    <td style={S.tdR}>{brl(l.media)}</td>
                    <td style={S.tdR}>{l.media_kg ? brl(l.media_kg) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Seção LOCAIS */}
            {dados.locais && dados.locais.length > 0 && (
              <table style={S.tbl}>
                <tbody>
                  <tr style={S.secRow}>
                    <td style={{ ...S.td, width: '39%' }} colSpan={3}><strong>LOCAIS</strong></td>
                    <td style={{ ...S.tdC, width: '8%' }}></td>
                    <td style={{ ...S.tdC, width: '10%' }}></td>
                    <td style={{ ...S.tdR, width: '15%' }}></td>
                    <td style={{ ...S.tdR, width: '13%' }}></td>
                    <td style={{ ...S.tdR, width: '13%' }}></td>
                  </tr>
                  {dados.locais.map((l, i) => (
                    <tr key={i}>
                      <td style={S.td} colSpan={3}>{l.local}</td>
                      <td style={S.tdC}>{l.lotes}</td>
                      <td style={S.tdC}><strong>{l.qtd}</strong></td>
                      <td style={S.tdR}>{brl(l.valor)}</td>
                      <td style={S.tdR}>{brl(l.media)}</td>
                      <td style={S.tdR}>{l.media_kg ? brl(l.media_kg) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* Seção SEXOS */}
            {dados.sexos && dados.sexos.length > 0 && (
              <table style={S.tbl}>
                <tbody>
                  <tr style={S.secRow}>
                    <td style={{ ...S.td, width: '39%' }} colSpan={3}><strong>SEXOS</strong></td>
                    <td style={{ ...S.tdC, width: '8%' }}></td>
                    <td style={{ ...S.tdC, width: '10%' }}></td>
                    <td style={{ ...S.tdR, width: '15%' }}></td>
                    <td style={{ ...S.tdR, width: '13%' }}></td>
                    <td style={{ ...S.tdR, width: '13%' }}></td>
                  </tr>
                  {dados.sexos.map((s, i) => (
                    <tr key={i}>
                      <td style={S.td} colSpan={3}>{s.sexo}</td>
                      <td style={S.tdC}>{s.lotes}</td>
                      <td style={S.tdC}><strong>{s.qtd}</strong></td>
                      <td style={S.tdR}>{brl(s.valor)}</td>
                      <td style={S.tdR}>{brl(s.media)}</td>
                      <td style={S.tdR}>{s.media_kg ? brl(s.media_kg) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* Resumo: Vendas / Defesas / Totais */}
            <table style={{ ...S.tbl, marginTop: 12 }}>
              <tbody>
                {dados.resumo.map((r, i) => (
                  <tr key={i} style={i === dados.resumo.length - 1 ? S.totRow : { fontWeight: 700 }}>
                    <td style={{ ...S.td, width: '15%' }}><strong>{r.label}</strong></td>
                    <td style={{ ...S.tdC, width: '24%' }}>{r.pct}</td>
                    <td style={{ ...S.tdC, width: '8%' }}>{r.lotes}</td>
                    <td style={{ ...S.tdC, width: '10%' }}><strong>{r.qtd}</strong></td>
                    <td style={{ ...S.tdR, width: '15%' }}>{brl(r.valor)}</td>
                    <td style={{ ...S.tdR, width: '13%' }}>{brl(r.media)}</td>
                    <td style={{ ...S.tdR, width: '13%' }}>{r.media_kg ? brl(r.media_kg) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={S.footer}>www.rematei.com.br</div>
          </div>
        </div>
      )}

      {/* Modal PDF */}
      {showPdf && pdfUrl && (
        <div className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center z-50 sm:p-4">
          <div className="bg-white w-full max-w-5xl flex flex-col rounded-t-2xl sm:rounded-xl shadow-2xl" style={{ height: '92dvh' }}>
            <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-200 bg-gray-50 rounded-t-2xl sm:rounded-t-xl flex-shrink-0">
              <span className="font-semibold text-gray-800 text-sm truncate flex-1 min-w-0">Balanço de Vendas — {leilaoAtual?.nome}</span>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <button onClick={() => downloadPdf(pdfUrl, `Balanço de Vendas - ${leilaoAtual?.nome || ''}.pdf`)}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white text-sm font-medium rounded-lg transition-colors">
                  Salvar PDF
                </button>
                <button onClick={printPdf} className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors">
                  <Printer size={15} /> Imprimir
                </button>
                <button onClick={closePdf} className="px-3 py-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors text-sm">Fechar</button>
              </div>
            </div>
            <div className="flex-1 overflow-hidden rounded-b-xl">
              <iframe ref={iframeRef} src={pdfUrl} title="Balanço de Vendas" style={{ width: '100%', height: '100%', border: 'none' }} />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
